import { get } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import {
  BsArrowCounterclockwise,
  BsArrowRepeat,
} from 'react-icons/bs'
import { DATA_LOADER_MODE } from '../../hooks/useDataLoader'
import { getLoadParamsFromUrl, clearUrlState } from '../../services/urlState'
import DataGrid from '../DataGrid/DataGrid'
import DataLibraryModal from '../DataLibraryModal/DataLibraryModal'
import ParsingOptions from '../ParsingOptions'
import WorkflowMobileSheet from '../WorkflowSteps/WorkflowMobileSheet'
import styles from './DataLoader.module.scss'

const LOAD_TABS = [
  { id: 'parsing', label: 'Parsing' },
  { id: 'filter', label: 'Filter' },
  { id: 'insights', label: 'Insights' },
]
import Loading from './loading'
import WarningMessage from '../WarningMessage'
import DataMismatchModal from './DataMismatchModal'
import DataSourcePicker from './DataSourcePicker'
import { tsvFormat } from 'd3-dsv'
import { CopyToClipboardButton } from '../CopyToClipboardButton'

function DataLoader({
  onHome,
  userInput,
  setUserInput,
  userData,
  userDataType,
  parseError,
  unstackedColumns,
  separator,
  setSeparator,
  thousandsSeparator,
  setThousandsSeparator,
  decimalsSeparator,
  setDecimalsSeparator,
  locale,
  setLocale,
  stackDimension,
  dataSource,
  data,
  loading,
  coerceTypes,
  loadSample,
  handleInlineEdit,
  handleTranspose,
  handleStackOperation,
  setJsonData,
  resetDataLoader,
  dataLoaderMode,
  startDataReplace,
  cancelDataReplace,
  commitDataReplace,
  replaceRequiresConfirmation,
  hydrateFromProject,
  autoRefreshEnabled,
  setAutoRefreshEnabled,
  autoRefreshInterval,
  setAutoRefreshInterval,
  lastRefreshedAt,
  setLastRefreshedAt,
  filtersSlot = null,
  insightsSlot = null,
  mobileActions = null,
}) {
  const [loadingError, setLoadingError] = useState()
  const [initialOptionState, setInitialOptionState] = useState(null)
  const [isMobileLayout, setIsMobileLayout] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  )
  const [showLibrary, setShowLibrary] = useState(false)
  const [parsingOpen, setParsingOpen] = useState(false)
  const [activeLoadTab, setActiveLoadTab] = useState('parsing')

  // The picker owns presentation; this state only preserves the selected source
  // for deep links and the "Change data" action.
  const [activeTab, setActiveTab] = useState('upload')

  // Deep-link: ?load=library or ?load=url&connector=X&...
  useEffect(() => {
    const lp = getLoadParamsFromUrl()
    if (!lp) return
    clearUrlState()
    if (lp.type === 'library') {
      setShowLibrary(true)
    } else if (lp.type === 'url') {
      setActiveTab('url')
      if (lp.connector) {
        setInitialOptionState({
          type: 'url',
          apiSourceId: lp.connector,
          connParams: { [lp.connector]: lp.params },
        })
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const mediaQuery = window.matchMedia('(max-width: 767px)')
    const handleChange = () => setIsMobileLayout(mediaQuery.matches)
    handleChange()
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
    mediaQuery.addListener(handleChange)
    return () => mediaQuery.removeListener(handleChange)
  }, [])

  const reloadRAW = useCallback(() => {
    window.location.replace(window.location.pathname)
  }, [])

  const copyToClipboardButton = !!userData ? (
    <CopyToClipboardButton content={tsvFormat(userData)} />
  ) : null

  // #TODO: memoize/move to component?
  function parsingErrors(data) {
    const errors = get(data, 'errors', [])
    const successRows = data.dataset.length - errors.length
    const row = errors[0].row + 1
    const column = Object.keys(errors[0].error)[0]
    return (
      <span>
        Ops, please check <span className="font-weight-bold">row {row}</span> at
        column <span className="font-weight-bold">{column}</span>.{' '}
        {errors.length === 2 && (
          <>
            {' '}
            There's another issue at row{' '}
            <span className="font-weight-bold">{errors[1].row + 1}</span>.{' '}
          </>
        )}
        {errors.length > 2 && (
          <>
            {' '}
            There are issues in{' '}
            <span className="font-weight-bold">{errors.length - 1}</span> more
            rows.{' '}
          </>
        )}
        {successRows > 0 && (
          <>
            The remaining{' '}
            <span className="font-weight-bold">
              {successRows} row{successRows > 1 && <>s</>}
            </span>{' '}
            look{successRows === 1 && <>s</>} fine.
          </>
        )}
      </span>
    )
  }

  const dataActionBar =
    userData && data ? (
      <div className={styles['data-action-bar']}>
        <div
          className={`d-flex justify-content-center align-items-center ${styles['start-over']} ${styles['loaded-action-button']} ${styles['action-reset']} user-select-none cursor-pointer`}
          onClick={reloadRAW}
        >
          <BsArrowRepeat className="mr-2" />
          <h4 className="m-0 d-inline-block">Reset</h4>
        </div>
        <div
          className={`d-flex justify-content-center align-items-center ${styles['start-over']} ${styles['loaded-action-button']} ${styles['action-change']} user-select-none cursor-pointer`}
          onClick={() => {
            setInitialOptionState(dataSource)
            const srcType = dataSource?.type
            if (['url', 'sparql', 'project'].includes(srcType)) {
              setActiveTab(srcType)
            } else if (srcType === 'paste') {
              setActiveTab('paste')
            } else if (srcType === 'upload') {
              setActiveTab('upload')
            } else {
              setActiveTab('sample')
            }
            startDataReplace()
          }}
        >
          <BsArrowCounterclockwise className="mr-2" />
          <h4 className="m-0 d-inline-block">Change data</h4>
        </div>
      </div>
    ) : null

  const parsingOptionsPanel =
    userData && data ? (
      <div className={styles['loaded-parsing-panel']}>
        <button
          type="button"
          className={styles['parsing-toggle']}
          onClick={() => setParsingOpen((o) => !o)}
          aria-expanded={parsingOpen}
        >
          <span>Data parsing options</span>
          <svg
            className={`${styles['parsing-chevron']} ${parsingOpen ? styles['parsing-chevron-open'] : ''}`}
            width="14" height="14" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        {parsingOpen && (
          <div className={styles['parsing-body']}>
            <ParsingOptions
              locale={locale}
              setLocale={setLocale}
              separator={separator}
              setSeparator={setSeparator}
              thousandsSeparator={thousandsSeparator}
              setThousandsSeparator={setThousandsSeparator}
              decimalsSeparator={decimalsSeparator}
              setDecimalsSeparator={setDecimalsSeparator}
              dimensions={data ? unstackedColumns || data.dataTypes : []}
              stackDimension={stackDimension}
              setStackDimension={handleStackOperation}
              userDataType={userDataType}
              dataSource={dataSource}
              onDataRefreshed={(rawInput) => setUserInput(rawInput, dataSource)}
              autoRefreshEnabled={autoRefreshEnabled}
              setAutoRefreshEnabled={setAutoRefreshEnabled}
              autoRefreshInterval={autoRefreshInterval}
              setAutoRefreshInterval={setAutoRefreshInterval}
              lastRefreshedAt={lastRefreshedAt}
              setLastRefreshedAt={setLastRefreshedAt}
            />
          </div>
        )}
      </div>
    ) : null

  // DataGrid (shown when data is loaded)
  const dataGrid =
    userData && data ? (
      <DataGrid
        userDataset={userData}
        dataset={data.dataset}
        errors={data.errors}
        dataTypes={data.dataTypes}
        coerceTypes={coerceTypes}
        onDataUpdate={handleInlineEdit}
        onTranspose={handleTranspose}
      />
    ) : loading && !data ? (
      <Loading />
    ) : null



  // ── Mobile sheet mode (data loaded) ───────────────────────────────────────
  if (isMobileLayout && userData && data) {
    const warnings = (
      <>
        {data && !parseError && get(data, 'errors', []).length === 0 && (
          <WarningMessage variant="success" message={<span><span className="font-weight-bold">{data.dataset.length} rows</span> ({data.dataset.length * Object.keys(data.dataTypes).length} cells) parsed successfully.</span>} action={copyToClipboardButton} />
        )}
        {parseError && <WarningMessage variant="danger" message={parseError} action={copyToClipboardButton} />}
        {get(data, 'errors', []).length > 0 && <WarningMessage variant="warning" message={parsingErrors(data)} action={copyToClipboardButton} />}
        {loadingError && <WarningMessage variant="danger" message={loadingError} action={copyToClipboardButton} />}
      </>
    )

    const parsingTabContent = (
      <div className={styles['mobile-parsing-tab']}>
        {warnings}
        <ParsingOptions
          locale={locale}
          setLocale={setLocale}
          separator={separator}
          setSeparator={setSeparator}
          thousandsSeparator={thousandsSeparator}
          setThousandsSeparator={setThousandsSeparator}
          decimalsSeparator={decimalsSeparator}
          setDecimalsSeparator={setDecimalsSeparator}
          dimensions={data ? unstackedColumns || data.dataTypes : []}
          stackDimension={stackDimension}
          setStackDimension={handleStackOperation}
          userDataType={userDataType}
          dataSource={dataSource}
          onDataRefreshed={(rawInput) => setUserInput(rawInput, dataSource)}
          autoRefreshEnabled={autoRefreshEnabled}
          setAutoRefreshEnabled={setAutoRefreshEnabled}
          autoRefreshInterval={autoRefreshInterval}
          setAutoRefreshInterval={setAutoRefreshInterval}
          lastRefreshedAt={lastRefreshedAt}
          setLastRefreshedAt={setLastRefreshedAt}
        />
      </div>
    )

    return (
      <>
        <WorkflowMobileSheet
          showFullscreenBtn={false}
          mainContent={
            <div className={styles['mobile-grid-with-actions']}>
              {dataActionBar}
              {dataGrid}
            </div>
          }
          tabs={LOAD_TABS}
          activeTab={activeLoadTab}
          onTabChange={setActiveLoadTab}
          sheetContent={
            activeLoadTab === 'parsing' ? parsingTabContent :
            activeLoadTab === 'filter' ? filtersSlot :
            insightsSlot
          }
          actions={mobileActions}
        />
        {replaceRequiresConfirmation && (
          <DataMismatchModal
            replaceRequiresConfirmation={replaceRequiresConfirmation}
            commitDataReplace={commitDataReplace}
            cancelDataReplace={cancelDataReplace}
          />
        )}
        <DataLibraryModal
          open={showLibrary}
          onClose={() => setShowLibrary(false)}
          onHome={onHome ? () => { setShowLibrary(false); onHome() } : undefined}
          onLoad={(text, delimiter, dataset) => {
            loadSample(text, delimiter, { chartTypes: dataset.chartTypes, datasetTitle: dataset.title, datasetSrc: dataset.src })
            setActiveTab('sample')
            setShowLibrary(false)
          }}
        />
      </>
    )
  }

  return (
    <>
      <Row>
        <Col>
          <Row className="h-100">
            <Col className="h-100">
              {!userData ? (
                <DataSourcePicker
                  activeSource={activeTab}
                  onSourceChange={setActiveTab}
                  userInput={userInput}
                  userData={userData}
                  userDataType={userDataType}
                  setUserInput={setUserInput}
                  setLoadingError={setLoadingError}
                  setJsonData={setJsonData}
                  initialOptionState={initialOptionState}
                  onProjectSelected={hydrateFromProject}
                  onBrowseLibrary={() => setShowLibrary(true)}
                />
              ) : (
                <>
                  {dataActionBar}
                  {parsingOptionsPanel}
                  {filtersSlot}
                  {dataGrid}
                </>
              )}

              {(!isMobileLayout || userData) &&
                data && !parseError && get(data, 'errors', []).length === 0 && (
                <WarningMessage
                  variant="success"
                  message={
                    <span>
                      <span className="font-weight-bold">
                        {data.dataset.length} rows
                      </span>{' '}
                      (
                      {data.dataset.length * Object.keys(data.dataTypes).length}{' '}
                      cells) have been successfully parsed, now you can choose a
                      chart!
                    </span>
                  }
                  action={copyToClipboardButton}
                />
              )}

              {(!isMobileLayout || userData) && parseError && (
                <WarningMessage
                  variant="danger"
                  message={parseError}
                  action={copyToClipboardButton}
                />
              )}

              {(!isMobileLayout || userData) && get(data, 'errors', []).length > 0 && (
                <WarningMessage
                  variant="warning"
                  message={parsingErrors(data)}
                  action={copyToClipboardButton}
                />
              )}

              {(!isMobileLayout || userData) && loadingError && (
                <WarningMessage
                  variant="danger"
                  message={loadingError}
                  action={copyToClipboardButton}
                />
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      {replaceRequiresConfirmation && (
        <DataMismatchModal
          replaceRequiresConfirmation={replaceRequiresConfirmation}
          commitDataReplace={commitDataReplace}
          cancelDataReplace={cancelDataReplace}
        />
      )}
      <DataLibraryModal
        open={showLibrary}
        onClose={() => setShowLibrary(false)}
        onHome={onHome ? () => { setShowLibrary(false); onHome() } : undefined}
        onLoad={(text, delimiter, dataset) => {
          loadSample(text, delimiter, {
            chartTypes: dataset.chartTypes,
            datasetTitle: dataset.title,
            datasetSrc: dataset.src,
          })
          setActiveTab('sample')
          setShowLibrary(false)
        }}
      />
    </>
  )
}

export default React.memo(DataLoader)
